import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Bank, Plus, Trash, CurrencyDollar, CheckCircle, Clock, CaretDown, CaretUp, X
} from '@phosphor-icons/react';

export default function LoansPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loans, setLoans] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRepay, setShowRepay] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({ staff_id: '', amount: '', reason: '', date: new Date().toISOString().slice(0, 10), emi_amount: '', notes: '' });
  const [repayForm, setRepayForm] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), method: 'salary_deduction', notes: '' });

  const fetchData = async () => {
    try {
      const [loansRes, staffRes] = await Promise.all([api.get('/loans', { params: { status: statusFilter } }), api.get('/staff')]);
      setLoans(loansRes.data); setStaffList(staffRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api, statusFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/loans', { ...form, amount: Number(form.amount), emi_amount: Number(form.emi_amount || 0) });
      setShowForm(false); setForm({ staff_id: '', amount: '', reason: '', date: new Date().toISOString().slice(0, 10), emi_amount: '', notes: '' }); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleRepay = async (loanId) => {
    try {
      await api.post(`/loans/${loanId}/repay`, { ...repayForm, amount: Number(repayForm.amount) });
      setShowRepay(null); setRepayForm({ amount: '', date: new Date().toISOString().slice(0, 10), method: 'salary_deduction', notes: '' }); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => { try { await api.delete(`/loans/${id}`); fetchData(); } catch (e) { console.error(e); } };

  const totalActive = loans.filter(l => l.status === 'active');
  const totalOutstanding = totalActive.reduce((s, l) => s + l.remaining, 0);
  const totalEMI = totalActive.reduce((s, l) => s + (l.emi_amount || 0), 0);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="loans-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 right-0 z-0" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Staff Finance</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('staff_loans')}</h1>
          <p className="text-sm text-neutral-500 mt-1">{totalActive.length} active &middot; Outstanding Rs.{totalOutstanding.toLocaleString('en-IN')} &middot; Monthly EMI Rs.{totalEMI.toLocaleString('en-IN')}</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-loan-btn" className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit"><Plus size={16} /> {t('add_loan')}</button>
      </div>

      <div className="flex gap-2 relative z-10">
        {['', 'active', 'repaid'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${statusFilter === s ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/30' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`}>
            {s === '' ? 'All' : s === 'active' ? t('active') : t('repaid')}
          </button>
        ))}
      </div>

      <div className="space-y-3 relative z-10">
        {loans.length === 0 ? (
          <div className="text-center py-16"><Bank size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" /><p className="text-neutral-500 text-sm">No loans found</p></div>
        ) : loans.map(loan => {
          const isActive = loan.status === 'active';
          const paidPct = loan.amount > 0 ? Math.round(((loan.amount - loan.remaining) / loan.amount) * 100) : 100;
          const isExpanded = expandedId === loan.loan_id;
          return (
            <div key={loan.loan_id} className={`glass-card p-5 border-l-2 ${isActive ? 'border-amber-500/30' : 'border-emerald-500/30'} animate-fade-up`} data-testid={`loan-card-${loan.loan_id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
                    {isActive ? <Clock size={20} weight="duotone" className="text-amber-400" /> : <CheckCircle size={20} weight="duotone" className="text-emerald-400" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-white">{loan.staff_name}</p>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${isActive ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>{loan.status}</span>
                    </div>
                    <p className="text-[11px] text-neutral-500">{loan.reason || 'Salary Advance'} &middot; {loan.date}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <div>
                        <p className="text-xs text-neutral-500">{t('amount')}</p>
                        <p className="text-sm font-semibold text-white">Rs.{loan.amount.toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500">{t('remaining')}</p>
                        <p className="text-sm font-semibold text-white">Rs.{loan.remaining.toLocaleString('en-IN')}</p>
                      </div>
                      {loan.emi_amount > 0 && (
                        <div>
                          <p className="text-xs text-neutral-500">{t('emi')}</p>
                          <p className="text-sm font-semibold text-white">Rs.{loan.emi_amount.toLocaleString('en-IN')}/mo</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className={`h-full rounded-full ${isActive ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${paidPct}%` }} />
                    </div>
                    <p className="text-[10px] text-neutral-500 mt-1">{paidPct}% repaid</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isActive && (
                    <button onClick={() => { setRepayForm({ amount: loan.emi_amount || '', date: new Date().toISOString().slice(0, 10), method: 'salary_deduction', notes: '' }); setShowRepay(loan.loan_id); }}
                      data-testid={`repay-${loan.loan_id}`} className="px-4 py-2 rounded-full border border-emerald-500/20 text-xs font-medium text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1 transition-colors">
                      <CurrencyDollar size={14} /> {t('repay')}
                    </button>
                  )}
                  {loan.repayments && loan.repayments.length > 0 && (
                    <button onClick={() => setExpandedId(isExpanded ? null : loan.loan_id)} className="px-3 py-2 rounded-full border border-white/10 text-xs text-neutral-400 hover:bg-white/5 flex items-center gap-1 transition-colors">
                      {loan.repayments.length} {isExpanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
                    </button>
                  )}
                  <button onClick={() => handleDelete(loan.loan_id)} className="p-2 rounded-full border border-white/10 text-neutral-500 hover:text-red-400 hover:border-red-400/20 transition-colors"><Trash size={14} /></button>
                </div>
              </div>

              {isExpanded && loan.repayments && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                  <p className="overline mb-2">Repayment History</p>
                  {loan.repayments.slice().reverse().map((r, i) => (
                    <div key={i} className="flex items-center gap-4 text-xs text-neutral-400 bg-white/[0.02] rounded-xl px-4 py-2.5">
                      <span className="text-neutral-300 font-mono">{r.date}</span>
                      <span>Rs.{r.amount?.toLocaleString('en-IN')}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5">{r.method}</span>
                      {r.notes && <span className="text-neutral-500">{r.notes}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showRepay && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowRepay(null)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">Record Repayment</h3>
            <div className="space-y-4">
              <div><label className="overline block mb-2">{t('amount')} (Rs.)</label><input type="number" value={repayForm.amount} onChange={e => setRepayForm({...repayForm, amount: e.target.value})} required data-testid="repay-amount" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div><label className="overline block mb-2">{t('date')}</label><input type="date" value={repayForm.date} onChange={e => setRepayForm({...repayForm, date: e.target.value})} data-testid="repay-date" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div><label className="overline block mb-2">Method</label><select value={repayForm.method} onChange={e => setRepayForm({...repayForm, method: e.target.value})} data-testid="repay-method" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50"><option value="salary_deduction">Salary Deduction</option><option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option></select></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowRepay(null)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button onClick={() => handleRepay(showRepay)} data-testid="confirm-repay-btn" className="flex-1 py-3 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors">Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{t('add_loan')}</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="overline block mb-2">Staff Member</label><select value={form.staff_id} onChange={e => setForm({...form, staff_id: e.target.value})} required data-testid="loan-staff-select" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50"><option value="">Select staff</option>{staffList.filter(s => s.status === 'active').map(s => <option key={s.staff_id} value={s.staff_id}>{s.name} ({s.role})</option>)}</select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline block mb-2">{t('amount')} (Rs.)</label><input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required data-testid="loan-amount" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
                <div><label className="overline block mb-2">{t('emi')} (Rs./mo)</label><input type="number" value={form.emi_amount} onChange={e => setForm({...form, emi_amount: e.target.value})} data-testid="loan-emi" placeholder="0 = lump sum" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              </div>
              <div><label className="overline block mb-2">Reason</label><input type="text" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} data-testid="loan-reason" placeholder="e.g., Medical emergency, Festival advance" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div><label className="overline block mb-2">{t('date')}</label><input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="loan-date" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-loan-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
